import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import WeekViewPage from "../WeekViewPage/WeekViewPage";
import { fetchEvents, fetchAppData } from "../../../api/calendars";
import type { AppData, FetchResult, FormattedEvent, FetchError } from "../../../types";
import { getTodayString } from "../../../functions/date";
import AppHeaderMenu from "../AppHeaderMenu/AppHeaderMenu";
import { CalendarGroupProvider } from "../DataProvider/CalenderGroupContext/CalenderGroupContext";
import { LiveProvider } from "../DataProvider/LiveContext/LiveContext";
import "./VereinsCalendar.css";

const LOCAL_STORAGE_KEY = "vereinsCalendarLocalStorage";

function loadCache(verein: string) {
  const raw = localStorage.getItem(`${LOCAL_STORAGE_KEY}_${verein}`);
  return raw ? JSON.parse(raw) : null;
}

function saveCache(verein: string, vereinsData: AppData, calenders: any) {
  localStorage.setItem(
    `${LOCAL_STORAGE_KEY}_${verein}`,
    JSON.stringify({ vereinsData, calenders })
  );
}

function removeByProperty<T, K extends keyof T>(
  source: T[],
  remove: T[],
  prop: K
): T[] {
  const removeSet = new Set(remove.map(item => item[prop]));
  return source.filter(item => !removeSet.has(item[prop]));
};


function mergeEvents(fetchResult: FetchResult[], cached: any) {
  const allEvents: FormattedEvent[] = [];
  const dataForLocalStorage: any[] = [];

  fetchResult.forEach((result) => {
    const cachedEvents =
      cached?.calenders?.find((c: any) => c.calender === result.calender)?.events || [];

    let appEvents: FormattedEvent[] = [];

    if (result.errors.length === 0) {
      appEvents = result.events;
    } else {
      appEvents = cachedEvents;
    }
    // only add those events which are not already in the allEvents.
    // this happens when two mannschaften from the same verein play each other
    appEvents = removeByProperty(appEvents, allEvents, "hash_id")
    allEvents.push(...appEvents);
    dataForLocalStorage.push({ calender: result.calender, events: appEvents });
  });

  return { allEvents, dataForLocalStorage };
}


const VereinsCalendar: React.FC = () => {
  const { verein } = useParams();
  const [loadingMessage, setLoadingMessage] = useState("");
  const [vereinsData, setVereinsData] = useState<AppData>({
    verein: { Slug: "", VereinsId: "", Name: "", LongName: "", Verband: "" },
    calendars: [],
  });
  const [events, setEvents] = useState<FormattedEvent[]>([]);
  const [fetchErrors, setFetchErrors] = useState<FetchError[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(true);
  const [day, setDay] = useState("");

  // Restore last day from localStorage
  useEffect(() => {
    // Check for #live hash to go to current week
    if (window.location.hash === "#live") {
      // set to current week
      setDay(getTodayString());
      // remove the hash from the URL without reloading
      history.replaceState(null, "", window.location.pathname + window.location.search);
    } else {
    const savedDay = localStorage.getItem("lastDay");
      if (savedDay) {
        setDay(savedDay);
      }
    }
  }, []);

  // Save whenever day changes
  useEffect(() => {
    localStorage.setItem("lastDay", day);
  }, [day]);


  // Load cached data immediately
  useEffect(() => {
    const cached = loadCache(verein!);
    if (cached) {
      setVereinsData(cached.vereinsData);
      const allEvents: FormattedEvent[] = cached.calenders?.flatMap((c: FetchResult) => c.events || []);
      allEvents.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      
      if (allEvents?.length) {
        setEvents(allEvents);
        setLoading(false);
      }
    }
  }, [verein]);

  // Fetch Vereins metadata
  useEffect(() => {
    let ignore = false;

    const fetchVereinsData = async () => {
      try {
        setLoadingMessage("Lade Verein");
        const fetched: AppData|null = await fetchAppData(verein!);

        if (!fetched || !fetched.verein?.VereinsId) {
          console.error("Invalid Vereins data from backend", fetched);
          throw new Error("Invalid Vereins data from backend");
        }
        document.title = `${fetched.verein.Name} - Termine`
        const cached = loadCache(verein!);
        const vereinsChanged =
          !cached || JSON.stringify(cached.vereinsData) !== JSON.stringify(fetched);

        if (vereinsChanged && !ignore) {
          setVereinsData(fetched);
        }

        // trigger events fetch
        if (!ignore) {
          fetchEventsForVerein(fetched, cached);
        }
      } catch (err) {
        console.error("Failed to fetch vereins data", err);
        setLoading(false);
        setFetching(false);
      }
    };

    fetchVereinsData();
    return () => {
      ignore = true;
    };
  }, [verein]);

  // Separate function for events
  async function fetchEventsForVerein(fetchedVereinsData: AppData, cached: any) {
    try {
      setLoadingMessage(`Lade Spiele und Termine von ${fetchedVereinsData.verein.LongName}`);

      const fetchResult = await fetchEvents(fetchedVereinsData);
      setFetchErrors(fetchResult.flatMap((r) => r.errors));

      const { allEvents, dataForLocalStorage } = mergeEvents(fetchResult, cached);
      allEvents.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      setEvents(allEvents);
      saveCache(verein!, fetchedVereinsData, dataForLocalStorage);
    } catch (err) {
      console.error("Failed to fetch events", err);
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }

  // Altersklassen groups
  const groups = useMemo(() => {
    const set = new Set<string>();
    vereinsData.calendars.forEach((c) => set.add(c.Team.Altersklasse));
    return Array.from(set);
  }, [vereinsData.calendars]);

  return (
      <div className="VereinsCalendar">
        <LiveProvider>
          <CalendarGroupProvider groups={groups}>
            <AppHeaderMenu
              onHeaderClick={setDay}
              displayedDay={day}
              vereinsData={vereinsData}
              fetching={fetching}
              fetchErrors={fetchErrors}
            />

            {loading && <LoadingOverlay message={loadingMessage} />}

            <WeekViewPage events={events} day={day} onChangeDay={setDay} />
          </CalendarGroupProvider>
        </LiveProvider>
      </div>
  );
};

export default VereinsCalendar;